import type { Conversation, ConversationState } from '../Conversation';
import { isToolMessage, isUserMessage } from '../types';

/**
 * Registry for storing reference material from conversation messages.
 * Used by SketchRootAgent to accumulate context from user and tool messages
 * that can be passed to video generation.
 */
export class ReferenceMaterialRegistry {
    private static instance: ReferenceMaterialRegistry | null = null;
    private content: string[] = [];
    private processedMessageCount: number = 0;
    private unsubscribe?: () => void;

    private constructor() {}

    public static getInstance(): ReferenceMaterialRegistry {
        if (!ReferenceMaterialRegistry.instance) {
            ReferenceMaterialRegistry.instance = new ReferenceMaterialRegistry();
        }
        return ReferenceMaterialRegistry.instance;
    }

    /**
     * Subscribe to conversation updates to automatically populate reference material
     */
    public subscribeToConversation(conversation: Conversation): void {
        // Unsubscribe from any previous conversation
        if (this.unsubscribe) {
            this.unsubscribe();
        }

        // Subscribe to conversation updates
        this.unsubscribe = conversation.subscribe((state) => {
            this.handleConversationUpdate(state);
        });
    }

    /**
     * Handle conversation updates and extract reference material
     */
    private handleConversationUpdate(state: ConversationState): void {
        // Process any new messages and add them to reference material
        const newMessages = state.messages.slice(this.processedMessageCount);

        for (const message of newMessages) {
            if (isUserMessage(message)) {
                // Extract text content from user messages
                const content =
                    typeof message.content === 'string'
                        ? message.content
                        : message.content
                              .filter((part) => part.type === 'text')
                              .map((part) => part.text)
                              .join('\n');

                if (content.trim()) {
                    this.addContent(`User: ${content}`);
                }
            } else if (isToolMessage(message)) {
                // Add tool results to reference material
                const content = typeof message.content === 'string' ? message.content : '';
                if (content.trim()) {
                    this.addContent(`Tool Result: ${content}`);
                }
            }
        }

        this.processedMessageCount = state.messages.length;
    }

    /**
     * Add content to the reference material registry
     */
    private addContent(content: string): void {
        if (content && content.trim()) {
            this.content.push(content.trim());
        }
    }

    /**
     * Get all reference material as a single concatenated string
     */
    public getContent(): string {
        return this.content.join('\n\n');
    }

    /**
     * Check if the registry has any content
     */
    public hasContent(): boolean {
        return this.content.length > 0;
    }

    /**
     * Clear all reference material and reset state
     */
    public clear(): void {
        this.content = [];
        this.processedMessageCount = 0;
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = undefined;
        }
    }

    /**
     * Destroy the singleton instance (useful for cleanup)
     */
    public static destroyInstance(): void {
        if (ReferenceMaterialRegistry.instance) {
            ReferenceMaterialRegistry.instance.clear();
            ReferenceMaterialRegistry.instance = null;
        }
    }
}
